import React, { useState, useRef, useEffect } from 'react';
import { useConversation } from '@elevenlabs/react';
import { Message } from './types';
import './App.css';

const App: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [agentId, setAgentId] = useState<string>('');
  const [tempAgentId, setTempAgentId] = useState<string>('');
  const [conversationTime, setConversationTime] = useState<number>(0); // Time in seconds
  const conversationEndRef = useRef<HTMLDivElement>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize agent ID from localStorage or environment
  useEffect(() => {
    const savedAgentId = localStorage.getItem('elevenlabs_agent_id');
    const defaultAgentId = import.meta.env.VITE_AGENT_ID || '';
    const initialAgentId = savedAgentId || defaultAgentId;
    setAgentId(initialAgentId);
    setTempAgentId(initialAgentId);
  }, []);

  const conversation = useConversation({
    onConnect: () => {
      console.log('Connected to ElevenLabs');
      setError(null);
      // Start the timer
      timerIntervalRef.current = setInterval(() => {
        setConversationTime(prev => prev + 1);
      }, 1000);
    },
    onDisconnect: () => {
      console.log('Disconnected from ElevenLabs');
      // Stop the timer
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    },
    onError: (error) => {
      console.error('Conversation error:', error);
      const errorMessage = typeof error === 'string' ? error : (error as Error).message || 'An error occurred';
      setError(errorMessage);
    },
    onMessage: (message) => {
      console.log('Message received:', message);
      
      // Add user message
      if (message.source === 'user' && message.message) {
        const newMessage: Message = {
          id: Date.now().toString() + '-user-' + Math.random(),
          text: message.message,
          sender: 'user',
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, newMessage]);
      }
      
      // Add assistant message
      if (message.source === 'ai' && message.message) {
        const newMessage: Message = {
          id: Date.now().toString() + '-ai-' + Math.random(),
          text: message.message,
          sender: 'assistant',
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, newMessage]);
      }
    },
    onModeChange: (mode) => {
      console.log('Mode changed:', mode);
    },
  });

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    conversationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const connect = async () => {
    if (!agentId) {
      setError('Please configure VITE_AGENT_ID in your .env file');
      return;
    }

    try {
      setError(null);
      await conversation.startSession({ 
        agentId: agentId,
        connectionType: 'webrtc'
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start conversation');
    }
  };

  const disconnect = async () => {
    await conversation.endSession();
  };

  const openSettings = () => {
    setTempAgentId(agentId);
    setShowSettings(true);
  };

  const closeSettings = () => {
    setShowSettings(false);
    setTempAgentId(agentId); // Reset to current value
  };

  const saveSettings = async () => {
    // Disconnect if currently connected before changing agent ID
    if (isConnected || isConnecting) {
      await conversation.endSession();
    }
    
    setAgentId(tempAgentId);
    localStorage.setItem('elevenlabs_agent_id', tempAgentId);
    setShowSettings(false);
  };

  const resetTimer = () => {
    setConversationTime(0);
  };

  // Format time in HH:MM:SS or MM:SS
  const formatTime = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hrs > 0) {
      return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);

  const isConnected = conversation.status === 'connected';
  const isConnecting = conversation.status === 'connecting';

  return (
    <div className="app">
      <div className="container">
        <div className="header">
          <h1>🎙️ Voice Agent (Elevenlabs)</h1>
          <button className="settings-btn" onClick={openSettings} title="Settings">
            ⚙️
          </button>
        </div>

        <div className={`status ${isConnected ? 'connected' : isConnecting ? 'connecting' : 'disconnected'}`}>
          {isConnected ? 'Connected - Start speaking!' : 
           isConnecting ? 'Connecting to voice agent...' : 
           'Disconnected - Click Connect to start'}
        </div>

        <div className="controls">
          <button 
            onClick={connect} 
            disabled={isConnected || isConnecting}
            className="connect-btn"
          >
            Connect
          </button>
          <button 
            onClick={disconnect} 
            disabled={!isConnected}
            className="disconnect-btn"
          >
            Disconnect
          </button>
        </div>

        {error && (
          <div className="status disconnected">
            Error: {error}
          </div>
        )}

        <div className="token-usage">
          <div className="token-header">
            <strong>⏱️ Conversation Time</strong>
            <button className="reset-tokens-btn" onClick={resetTimer}>
              Reset
            </button>
          </div>
          <div className="time-display">
            <div className="time-value">{formatTime(conversationTime)}</div>
            <div className="time-label">
              {isConnected ? 'Recording...' : 'Total Duration'}
            </div>
          </div>
        </div>

        <div className="conversation">
          {messages.map((message) => (
            <div key={message.id} className={`message ${message.sender}`}>
              {message.text}
            </div>
          ))}
          <div ref={conversationEndRef} />
        </div>

        {isConnected && (
          <div className="audio-indicator">
            <div className="audio-bars">
              <div className="bar"></div>
              <div className="bar"></div>
              <div className="bar"></div>
              <div className="bar"></div>
              <div className="bar"></div>
            </div>
            <p className="listening-text">🎤 Listening...</p>
          </div>
        )}

        <div className="info">
          <strong>💡 Tip:</strong> Make sure your microphone is enabled and allowed in your browser. 
          Simply speak naturally - the assistant will respond to you in real-time!
        </div>

        {showSettings && (
          <div className="modal-overlay" onClick={closeSettings}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>⚙️ Settings</h3>
                <button className="close-btn" onClick={closeSettings}>×</button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="agentId">ElevenLabs Agent ID:</label>
                  <input
                    type="text"
                    id="agentId"
                    value={tempAgentId}
                    onChange={(e) => setTempAgentId(e.target.value)}
                    placeholder="Enter your Agent ID"
                  />
                  <small>
                    Get your Agent ID from{' '}
                    <a href="https://elevenlabs.io/app/conversational-ai" target="_blank" rel="noopener noreferrer">
                      ElevenLabs Conversational AI
                    </a>
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button className="cancel-btn" onClick={closeSettings}>Cancel</button>
                <button className="save-btn" onClick={saveSettings}>Save</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default App;
